import React from 'react';
import { Box, Typography } from '@mui/material';

export type FormatKind = 'bold' | 'h1' | 'h2' | 'h3';

function renderInline(text: string): React.ReactNode[] {
  const parts = text.split(/\*\*(.+?)\*\*/g);
  return parts.map((p, i) =>
    i % 2 === 1 ? <strong key={i}>{p}</strong> : <React.Fragment key={i}>{p}</React.Fragment>
  );
}

export function renderCommentBody(body: string): JSX.Element {
  const lines = body.split('\n');
  return (
    <Box sx={{ '& > *': { mb: 0.5 } }}>
      {lines.map((line, idx) => {
        if (line.startsWith('### ')) {
          return (
            <Typography key={idx} variant="subtitle1" sx={{ fontWeight: 600, mt: 1 }}>
              {renderInline(line.slice(4))}
            </Typography>
          );
        }
        if (line.startsWith('## ')) {
          return (
            <Typography key={idx} variant="h6" sx={{ mt: 1.5 }}>
              {renderInline(line.slice(3))}
            </Typography>
          );
        }
        if (line.startsWith('# ')) {
          return (
            <Typography key={idx} variant="h5" sx={{ mt: 2 }}>
              {renderInline(line.slice(2))}
            </Typography>
          );
        }
        return (
          <Typography key={idx} variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {line ? renderInline(line) : '\u00A0'}
          </Typography>
        );
      })}
    </Box>
  );
}

export function applyFormat(
  el: HTMLTextAreaElement | HTMLInputElement | null,
  value: string,
  kind: FormatKind,
  onChange: (next: string) => void
): void {
  const start = el?.selectionStart ?? value.length;
  const end   = el?.selectionEnd   ?? value.length;

  if (kind === 'bold') {
    const selected = value.slice(start, end);
    const wrapped  = `**${selected}**`;
    const next     = value.slice(0, start) + wrapped + value.slice(end);
    onChange(next);
    const caret = selected.length > 0 ? start + wrapped.length : start + 2;
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(caret, caret);
    });
    return;
  }

  const prefix = kind === 'h1' ? '# ' : kind === 'h2' ? '## ' : '### ';
  const lineStart = value.lastIndexOf('\n', start - 1) + 1;
  const nlAfter   = value.indexOf('\n', start);
  const lineEnd   = nlAfter === -1 ? value.length : nlAfter;
  const line      = value.slice(lineStart, lineEnd);
  const stripped  = line.replace(/^#{1,3}\s/, '');
  const newLine   = prefix + stripped;
  const next      = value.slice(0, lineStart) + newLine + value.slice(lineEnd);
  onChange(next);
  const caret = lineStart + newLine.length;
  requestAnimationFrame(() => {
    el?.focus();
    el?.setSelectionRange(caret, caret);
  });
}
